import AdditionalAccounts from './AdditionalAccounts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { useAuth } from '../../../auth/AuthContext';
import { getCurrencyMeta } from '../../../utils/currency';
import { normalizeCadenceDiscountPct } from '../../../utils/calculateTotalFees';
import { getFunctionUrl } from '../../../utils/functionURLs';
import { buildRequiredTagGroups, getMissingRequiredTagGroups, getPricingTagNames } from '../../../utils/requiredTags';
import { formatCadenceOptionLabel } from '../../../utils/cadenceAdjustments';
import { isEditableTeamTag } from '../utils/learnToRow';
import { cadenceFrequencyLabel, getMembershipCadenceTransition, reconcileNoChangeCadencePresentation } from '../../../utils/membershipCadenceTransition';
import MembershipPaymentStatus from './MembershipPaymentStatus';
import { getMembershipPlanLabel, isFamilyMembershipMember } from '../../../utils/membershipReporting';
function buildCadenceOptions(setup = {}) {
  const mode = setup?.cadenceMode || 'fixed';
  if (mode === 'fixed') {
    const unit = setup?.fixedInterval?.unit === 'month' ? 'month' : 'year';
    return [{
      key: unit === 'month' ? 'monthly' : 'yearly',
      label: unit === 'month' ? 'Monthly' : 'Yearly',
      unit,
      count: 1,
      discountPct: 0
    }];
  }
  const memberChoice = setup?.memberChoice || {};
  return [{
    key: 'monthly',
    label: 'Monthly',
    unit: 'month',
    count: 1,
    discountPct: normalizeCadenceDiscountPct(memberChoice?.monthly?.discountPct)
  }, {
    key: 'yearly',
    label: 'Yearly',
    unit: 'year',
    count: 1,
    discountPct: normalizeCadenceDiscountPct(memberChoice?.yearly?.discountPct)
  }];
}
function normalizeMemberTagMap(rawTags) {
  if (Array.isArray(rawTags)) {
    return rawTags.reduce((acc, tagName) => {
      const key = typeof tagName === 'string' ? tagName.trim() : '';
      if (key) acc[key] = true;
      return acc;
    }, {});
  }
  if (rawTags && typeof rawTags === 'object') {
    return Object.entries(rawTags).reduce((acc, [tagName, enabled]) => {
      const key = typeof tagName === 'string' ? tagName.trim() : '';
      if (key && Boolean(enabled)) acc[key] = true;
      return acc;
    }, {});
  }
  return {};
}
function getAgeRuleTagNames(rule) {
  if (Array.isArray(rule?.tagNames)) {
    return rule.tagNames.filter(name => typeof name === 'string').map(name => name.trim()).filter(Boolean);
  }
  if (typeof rule?.tagName === 'string' && rule.tagName.trim() !== '') {
    return [rule.tagName.trim()];
  }
  return [];
}
function buildVisibleGroups(allTags = []) {
  const metaByName = new Map();
  const regularVisibilityByName = new Map();
  const ageRuleVisibleTagNames = new Set();
  for (const tag of allTags) {
    if (!tag || !isEditableTeamTag(tag)) continue;
    const name = typeof tag.name === 'string' ? tag.name.trim() : '';
    if (!name) continue;
    regularVisibilityByName.set(name, {
      visibleToMembers: tag.visibleToMembers !== false,
    });
    metaByName.set(name, {
      selfAssignable: Boolean(tag.selfAssignable),
      visibleToMembers: tag.visibleToMembers !== false,
      systemManaged: tag.systemManaged === true,
    });
  }
  for (const tag of allTags) {
    if (!tag || tag.type !== 'ageRule') continue;
    if (tag.visibleToMembers === false) continue;
    getAgeRuleTagNames(tag).forEach(name => {
      const regularMeta = regularVisibilityByName.get(name);
      if (regularMeta && regularMeta.visibleToMembers === false) return;
      ageRuleVisibleTagNames.add(name);
    });
  }
  return {
    metaByName,
    ageRuleVisibleTagNames
  };
}
function buildPricingTagGroups(tags = []) {
  const grouped = {};
  (Array.isArray(tags) ? tags : []).forEach(tag => {
    if (!tag || !isEditableTeamTag(tag)) return;
    const name = typeof tag.name === 'string' ? tag.name.trim() : '';
    if (!name) return;
    const category = typeof tag.category === 'string' && tag.category.trim() ? tag.category.trim() : 'Uncategorised';
    if (!grouped[category]) {
      grouped[category] = {
        category,
        multiAssignable: Boolean(tag.multiAssignable),
        tags: []
      };
    } else {
      grouped[category].multiAssignable = grouped[category].multiAssignable || Boolean(tag.multiAssignable);
    }
    if (!grouped[category].tags.find(entry => entry.name === name)) {
      grouped[category].tags.push({
        name,
        selfAssignable: Boolean(tag.selfAssignable)
      });
    }
  });
  return Object.values(grouped).map(group => ({
    ...group,
    tags: group.tags.sort((a, b) => a.name.localeCompare(b.name))
  })).sort((a, b) => a.category.localeCompare(b.category));
}
function groupSelectedTagsByCategory(groups = [], selectedTagNames = []) {
  const selectedSet = new Set((Array.isArray(selectedTagNames) ? selectedTagNames : []).map(tagName => typeof tagName === 'string' ? tagName.trim() : '').filter(Boolean));
  const next = {};
  groups.forEach(group => {
    next[group.category] = group.tags.map(tag => tag.name).filter(tagName => selectedSet.has(tagName));
  });
  return next;
}
function flattenSelectedTagsByCategory(selectedByCategory = {}) {
  return Object.values(selectedByCategory || {}).flat().map(tagName => typeof tagName === 'string' ? tagName.trim() : '').filter(Boolean);
}
function formatTimestamp(value) {
  const date = typeof value?.toDate === 'function' ? value.toDate() : value instanceof Date ? value : null;
  return date ? date.toLocaleString() : '';
}
function toDate(value) {
  if (!value) return null;
  const parsed = typeof value?.toDate === 'function' ? value.toDate() : value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function formatDateTime(value) {
  const date = toDate(value);
  return date ? date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '';
}
function formatFamilyMemberName(profile = {}) {
  const first = typeof profile?.firstName === 'string' ? profile.firstName.trim() : '';
  const last = typeof profile?.lastName === 'string' ? profile.lastName.trim() : '';
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const display = typeof profile?.displayName === 'string' ? profile.displayName.trim() : '';
  if (display) return display;
  const username = typeof profile?.username === 'string' ? profile.username.trim() : '';
  if (username) return username;
  return 'Family member';
}
function normalizeMembershipPlanType(value) {
  return value === 'family_tier' ? 'family_tier' : 'individual';
}
function parseFamilyTiers(setup = {}) {
  const tiers = Array.isArray(setup?.familyTiers) ? setup.familyTiers : [];
  return tiers.map((tier, index) => ({
    id: typeof tier?.id === 'string' && tier.id.trim() ? tier.id.trim() : `family_tier_${index + 1}`,
    label: typeof tier?.label === 'string' && tier.label.trim() ? tier.label.trim() : `Family ${index + 1}`,
    maxParents: Math.max(1, Number(tier?.maxParents || 1)),
    maxChildren: Math.max(0, Number(tier?.maxChildren || 0)),
    sortOrder: Number.isFinite(Number(tier?.sortOrder)) ? Number(tier.sortOrder) : index
  })).sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.label.localeCompare(b.label);
  });
}
function normalizeRequestedTargetIds(raw, currentUid = '') {
  const values = Array.isArray(raw) ? raw : [];
  const normalized = values.map(uid => typeof uid === 'string' ? uid.trim() : '').filter(Boolean);
  if (currentUid && !normalized.includes(currentUid)) {
    normalized.unshift(currentUid);
  }
  return Array.from(new Set(normalized));
}
function findCadenceOption(options = [], cadence = {}) {
  const key = typeof cadence?.key === 'string' ? cadence.key.trim() : '';
  const unit = typeof cadence?.unit === 'string' ? cadence.unit.trim().toLowerCase() : '';
  const count = Math.max(1, Number(cadence?.count || 1));
  if (key) {
    const byKey = options.find(option => option.key === key);
    if (byKey) return byKey;
  }
  if (unit) {
    const byUnitAndCount = options.find(option => option.unit === unit && Number(option.count || 1) === count);
    if (byUnitAndCount) return byUnitAndCount;
    const byUnit = options.find(option => option.unit === unit);
    if (byUnit) return byUnit;
  }
  return null;
}
function extractCadenceFromPayment(payment = {}, setup = {}) {
  const rawCadence = payment?.cadence && typeof payment.cadence === 'object' ? payment.cadence : null;
  const unit = rawCadence?.unit === 'month' || rawCadence?.unit === 'year' ? rawCadence.unit : payment?.cadenceUnit === 'month' || payment?.cadenceUnit === 'year' ? payment.cadenceUnit : '';
  if (!unit) return null;
  const count = Math.max(1, Number(rawCadence?.intervalCount || payment?.cadenceIntervalCount || 1));
  const fallbackDiscountPct = unit === 'month' ? Number(setup?.memberChoice?.monthly?.discountPct || 0) : Number(setup?.memberChoice?.yearly?.discountPct || 0);
  return {
    unit,
    count,
    discountPct: normalizeCadenceDiscountPct(rawCadence?.discountPct ?? payment?.cadenceDiscountPct ?? fallbackDiscountPct),
    key: unit === 'month' ? 'monthly' : 'yearly',
    label: unit === 'month' ? 'Monthly' : 'Yearly'
  };
}
function summarizeMembershipRequest({
  requestedPlanLabel,
  requestedPricingTag,
  requestedAdminTagNames,
  requestedTargetDisplayNames,
  requestedFamilyName,
  note
}) {
  const parts = [];
  if (requestedPlanLabel) {
    parts.push(`Requested ${requestedPlanLabel}.`);
  } else {
    parts.push('Requested a membership change.');
  }
  if (requestedPricingTag) {
    parts.push(`Pricing option: ${requestedPricingTag}.`);
  }
  if (Array.isArray(requestedAdminTagNames) && requestedAdminTagNames.length > 0) {
    parts.push(`Tags: ${requestedAdminTagNames.join(', ')}.`);
  }
  if (requestedFamilyName) {
    parts.push(`Family: ${requestedFamilyName}.`);
  }
  if (Array.isArray(requestedTargetDisplayNames) && requestedTargetDisplayNames.length > 0) {
    parts.push(`Cover: ${requestedTargetDisplayNames.join(', ')}.`);
  }
  if (note) {
    parts.push(note);
  }
  return parts.join(' ').trim();
}
function normalizePendingMembershipChange(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}
function formatMinorAmount(minor, currency = 'GBP') {
  const code = String(currency || 'GBP').toUpperCase();
  const {
    decimals
  } = getCurrencyMeta(code);
  const amount = Number(minor || 0) / Math.pow(10, decimals);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(amount);
  } catch {
    return `${amount.toFixed(decimals)} ${code}`;
  }
}
function buildMembershipPreviewCopy(preview, fallbackCurrency = 'GBP') {
  if (!preview || typeof preview !== 'object') return '';
  if (preview.requestType === 'membership_collection_route') return preview.summary || '';
  const currency = String(preview?.currency || fallbackCurrency || 'GBP').toUpperCase();
  const nextRenewalCopy = `Next renewal: ${formatMinorAmount(preview?.nextRenewalAmountMinor || 0, currency)}.`;
  if (preview.approvalRequired) {
    if (preview.effectiveMode === 'immediate_after_payment' && Number(preview?.priceDeltaNowMinor || 0) > 0) {
      return `Approval required before this change can be applied. If approved, you will need to pay ${formatMinorAmount(preview.priceDeltaNowMinor, currency)} to activate it. ${nextRenewalCopy}`;
    }
    return `Approval required before this change can be applied. ${nextRenewalCopy}`;
  }
  if (preview.changeKind === 'no_op') return 'No membership change is needed.';
  if (preview.effectiveMode === 'immediate_after_payment') {
    return `Payment required now to activate this change. Charge now: ${formatMinorAmount(preview?.priceDeltaNowMinor || 0, currency)}. ${nextRenewalCopy}`;
  }
  if (preview.effectiveMode === 'next_renewal') {
    const transition = getMembershipCadenceTransition(preview);
    if (transition.timeline) {
      return `${transition.timeline}. Next ${cadenceFrequencyLabel(transition.targetCadence)} renewal: ${formatMinorAmount(preview?.nextRenewalAmountMinor || 0, currency)}.`;
    }
    return `Scheduled for the next renewal. ${nextRenewalCopy}`;
  }
  return `This change can be applied immediately. ${nextRenewalCopy}`;
}
function normalizeLearnToRowConversion(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const snapshot = value?.conversionConfigSnapshot && typeof value.conversionConfigSnapshot === 'object' ? value.conversionConfigSnapshot : value;
  const fixedMembershipPricingTag = typeof snapshot?.fixedMembershipPricingTag === 'string' ? snapshot.fixedMembershipPricingTag.trim() : '';
  return {
    isActive: value?.isActive !== false,
    courseName: typeof value?.courseName === 'string' ? value.courseName.trim() : '',
    status: typeof value?.status === 'string' ? value.status.trim().toLowerCase() : '',
    conversionStartsAt: toDate(value?.conversionStartsAt || value?.finalSessionEndsAt || null),
    membershipPricingMode: snapshot?.membershipPricingMode === 'credit_course_fee' ? 'credit_course_fee' : 'full_price',
    membershipSelectionMode: snapshot?.membershipSelectionMode === 'fixed_pricing_tag' ? 'fixed_pricing_tag' : 'member_picks',
    fixedMembershipPricingTag,
    courseCreditEligibleMinor: Number(value?.courseCreditEligibleMinor ?? value?.paidAmountMinor ?? 0),
    courseCreditAppliedMinor: Number(value?.courseCreditAppliedMinor || 0),
    courseCreditRemainingMinor: Number(value?.courseCreditRemainingMinor || 0),
    pricingTagLocked: value?.pricingTagLocked === true || Boolean(fixedMembershipPricingTag && snapshot?.membershipSelectionMode === 'fixed_pricing_tag')
  };
}
function buildLearnToRowConversionCopy(conversion, currency = 'GBP') {
  if (!conversion?.isActive) return '';
  const parts = [];
  if (conversion.courseName) {
    parts.push(`Learn to Row course: ${conversion.courseName}.`);
  }
  if (conversion.membershipSelectionMode === 'fixed_pricing_tag' && conversion.fixedMembershipPricingTag) {
    parts.push(`Membership option is fixed to ${conversion.fixedMembershipPricingTag}.`);
  } else if (conversion.membershipSelectionMode === 'member_picks') {
    parts.push('Choose the membership option you want to move onto after the course.');
  }
  if (conversion.membershipPricingMode === 'credit_course_fee' && Number(conversion.courseCreditEligibleMinor || 0) > 0) {
    const creditEligible = Number(conversion.courseCreditEligibleMinor || 0);
    const creditApplied = Number(conversion.courseCreditAppliedMinor || 0);
    parts.push(`Course credit available: ${formatMinorAmount(creditEligible, currency)}.`);
    if (creditApplied > 0) {
      parts.push(`Applied to this option today: ${formatMinorAmount(creditApplied, currency)}.`);
      const remainingCredit = Number(conversion.courseCreditRemainingMinor || 0);
      if (remainingCredit > 0) {
        parts.push(`Remaining credit after this setup: ${formatMinorAmount(remainingCredit, currency)}.`);
      }
    }
  }
  return parts.join(' ').trim();
}
function buildLearnToRowCurrentSetupLabel(conversion) {
  if (!conversion?.isActive) return '';
  const courseName = typeof conversion?.courseName === 'string' ? conversion.courseName.trim() : '';
  if (/learn to row|l2r/i.test(courseName)) return courseName;
  return courseName ? `${courseName} Learn to Row` : 'Learn to Row';
}
function normalizeMembershipPricing(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return {
    requestDiscountCode: typeof value?.requestDiscountCode === 'string' ? value.requestDiscountCode.trim().toUpperCase() : '',
    appliedDiscounts: Array.isArray(value?.appliedDiscounts) ? value.appliedDiscounts : [],
    totalDiscountAppliedMinor: Number(value?.totalDiscountAppliedMinor || 0),
    storedCreditAvailableMinor: Number(value?.storedCreditAvailableMinor || 0),
    storedCreditAppliedMinor: Number(value?.storedCreditAppliedMinor || 0),
    remainingStoredCreditMinor: Number(value?.remainingStoredCreditMinor || 0),
    nextRenewalDiscountAppliedMinor: Number(value?.nextRenewalDiscountAppliedMinor || 0)
  };
}
function buildMembershipPricingCopy(pricing, currency = 'GBP') {
  if (!pricing) return '';
  const parts = [];
  if (pricing.requestDiscountCode) {
    parts.push(`Discount code: ${pricing.requestDiscountCode}.`);
  }
  if (pricing.totalDiscountAppliedMinor > 0) {
    parts.push(`Discounts applied today: ${formatMinorAmount(pricing.totalDiscountAppliedMinor, currency)}.`);
  }
  if (pricing.storedCreditAvailableMinor > 0) {
    parts.push(`Stored credit available: ${formatMinorAmount(pricing.storedCreditAvailableMinor, currency)}.`);
    if (pricing.storedCreditAppliedMinor > 0) {
      parts.push(`Stored credit applied today: ${formatMinorAmount(pricing.storedCreditAppliedMinor, currency)}.`);
    }
    if (pricing.remainingStoredCreditMinor > 0) {
      parts.push(`Stored credit remaining after this payment: ${formatMinorAmount(pricing.remainingStoredCreditMinor, currency)}.`);
    }
  }
  if (pricing.nextRenewalDiscountAppliedMinor > 0) {
    parts.push(`Persistent discounts on future renewals: ${formatMinorAmount(pricing.nextRenewalDiscountAppliedMinor, currency)}.`);
  }
  return parts.join(' ').trim();
}
function getPreviewOutcomeLabel(preview) {
  if (!preview) return '';
  if (preview.approvalRequired) return 'Approval required';
  if (preview.effectiveMode === 'immediate_after_payment') return 'No approval needed, payment required';
  if (preview.effectiveMode === 'next_renewal') return 'Scheduled for next renewal';
  return 'No approval needed, applies immediately';
}
function getSubmitButtonLabel(preview, activeRequest) {
  if (!preview) return activeRequest?.status === 'requested' ? 'Update Request' : 'Save Change';
  if (preview.approvalRequired) return activeRequest?.status === 'requested' ? 'Update Request' : 'Send Request';
  if (preview.effectiveMode === 'immediate_after_payment') return 'Continue to Payment';
  if (preview.effectiveMode === 'next_renewal') return 'Schedule Change';
  return 'Apply Change';
}
export default function ManageMembershipCard({
  teamId,
  teamName,
  memberName,
  adminIds = [],
  adminNames = [],
  allTags = [],
  memberTags = {},
  selfManaged = false,
  reconciliationEnabled = false,
  membershipMode = 'hosted_stripe',
  autoOpen = false,
  onAutoOpenHandled,
  canLeave = true,
  onLeave
}) {
  const {
    user
  } = useAuth();
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestStatus, setRequestStatus] = useState('');
  const [requestError, setRequestError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [leaveStatus, setLeaveStatus] = useState('');
  const [activeRequest, setActiveRequest] = useState(null);
  const [stripeSetup, setStripeSetup] = useState(null);
  const [teamSettings, setTeamSettings] = useState(null);
  const [memberRecord, setMemberRecord] = useState(null);
  const [familyGroups, setFamilyGroups] = useState([]);
  const [familySelectionLoading, setFamilySelectionLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelPendingLoading, setCancelPendingLoading] = useState(false);
  const [requestedPlanType, setRequestedPlanType] = useState('individual');
  const [requestedPlanTierId, setRequestedPlanTierId] = useState('');
  const [requestedPricingTagsByCategory, setRequestedPricingTagsByCategory] = useState({});
  const [requestedFamilyId, setRequestedFamilyId] = useState('');
  const [requestedTargetUserIds, setRequestedTargetUserIds] = useState([]);
  const [requestNote, setRequestNote] = useState('');
  const [membershipDiscountCode, setMembershipDiscountCode] = useState('');
  const [selectedCadenceKey, setSelectedCadenceKey] = useState('');
  const [requestedCollectionRoute, setRequestedCollectionRoute] = useState('');
  const [inferredStoredCadence, setInferredStoredCadence] = useState(null);
  const [paidMembershipEnd, setPaidMembershipEnd] = useState(null);
  const memberTagMap = useMemo(() => normalizeMemberTagMap(memberTags), [memberTags]);
  const {
    metaByName,
    ageRuleVisibleTagNames
  } = useMemo(() => buildVisibleGroups(allTags), [allTags]);
  const currentAdminManagedTagNames = useMemo(() => Object.keys(memberTagMap).filter(name => memberTagMap[name]).filter(tagName => {
    const meta = metaByName.get(tagName);
    if (!meta) return ageRuleVisibleTagNames.has(tagName);
    if (meta.systemManaged) return false;
    return !meta.selfAssignable;
  }).sort((a, b) => a.localeCompare(b)), [ageRuleVisibleTagNames, memberTagMap, metaByName]);
  const familyTierOptions = useMemo(() => parseFamilyTiers(stripeSetup || {}), [stripeSetup]);
  const cadenceOptions = useMemo(() => buildCadenceOptions(stripeSetup || {}), [stripeSetup]);
  const selectedCadence = useMemo(() => cadenceOptions.find(option => option.key === selectedCadenceKey) || cadenceOptions[0] || null, [cadenceOptions, selectedCadenceKey]);
  const pricingTagOptions = useMemo(() => {
    const pricingTagNames = [...Object.keys(stripeSetup?.tagOverrides || {}), ...Object.keys(stripeSetup?.tagOverridesYearly || {})].map(name => typeof name === 'string' ? name.trim() : '').filter(Boolean);
    if (pricingTagNames.length === 0) return [];
    const tagsByName = new Map((Array.isArray(allTags) ? allTags : []).filter(tag => isEditableTeamTag(tag) && typeof tag?.name === 'string' && tag.name.trim() !== '').map(tag => [tag.name.trim(), tag]));
    return Array.from(new Set(pricingTagNames)).map(tagName => tagsByName.get(tagName) || {
      name: tagName,
      selfAssignable: false
    }).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [allTags, stripeSetup]);
  const pricingTagGroups = useMemo(() => buildPricingTagGroups(pricingTagOptions), [pricingTagOptions]);
  const requiredPricingTagGroups = useMemo(() => buildRequiredTagGroups(allTags || [], {
    pricingTagNames: getPricingTagNames(stripeSetup || {})
  }).filter(group => group.isPricingGroup), [allTags, stripeSetup]);
  const currentMembership = useMemo(() => {
    // The admin view reads coverage from the club member document. Keep billing
    // settings separate, and fall back to them for older member records.
    const coverageKeys = [
      'membershipPlanType', 'membershipPlanTierId', 'membershipPlanLabel',
      'membershipPlanSnapshot', 'membershipCoverageType', 'membershipCoverageFamilyId',
      'membershipCoveredMemberIds', 'membershipCoveredByUid', 'membershipCoveredByFamilyId',
      'membershipPaymentOwnerUid',
    ];
    const hasMemberCoverage = memberRecord && (
      memberRecord.membershipPlanType || memberRecord.membershipCoverageType
      || memberRecord.membershipCoveredByUid || memberRecord.membershipCoveredByFamilyId
      || memberRecord.membershipCoveredMemberIds?.length
    );
    if (!hasMemberCoverage) return teamSettings || {};
    return Object.fromEntries(coverageKeys.map(key => [key, memberRecord[key]]));
  }, [memberRecord, teamSettings]);
  const currentPlanType = isFamilyMembershipMember(currentMembership) ? 'family_tier' : 'individual';
  const pendingMembershipChange = useMemo(() => normalizePendingMembershipChange(teamSettings?.pendingMembershipChange), [teamSettings?.pendingMembershipChange]);
  const pendingBillingTransition = useMemo(() => normalizePendingMembershipChange(teamSettings?.pendingBillingTransition), [teamSettings?.pendingBillingTransition]);
  const billingPolicyTransition = useMemo(() => getMembershipCadenceTransition(pendingBillingTransition || {}), [pendingBillingTransition]);
  const displayedBillingPolicyTransition = useMemo(
    () => reconcileNoChangeCadencePresentation(billingPolicyTransition, inferredStoredCadence),
    [billingPolicyTransition, inferredStoredCadence]
  );
  const pendingMembershipStatus = typeof pendingMembershipChange?.status === 'string' ? pendingMembershipChange.status.trim().toLowerCase() : '';
  const canCancelPendingMembershipChange = ['pending_approval', 'approved_pending_payment', 'payment_in_progress', 'scheduled_for_renewal'].includes(pendingMembershipStatus);
  const membershipCurrency = String(stripeSetup?.currency || teamSettings?.currency || pendingMembershipChange?.currency || previewResult?.currency || 'GBP').toUpperCase();
  const pendingMembershipSummary = useMemo(() => buildMembershipPreviewCopy(pendingMembershipChange, membershipCurrency) || pendingMembershipChange?.summary || 'A membership change is currently being processed.', [membershipCurrency, pendingMembershipChange]);
  const pendingCadenceTransition = useMemo(() => getMembershipCadenceTransition(pendingMembershipChange || {}), [pendingMembershipChange]);
  const cadenceOverriddenByClub = Boolean(pendingMembershipChange?.cadencePolicyOverride);
  const flatRequestedPricingTagNames = useMemo(() => flattenSelectedTagsByCategory(requestedPricingTagsByCategory), [requestedPricingTagsByCategory]);
  const learnToRowConversion = useMemo(() => normalizeLearnToRowConversion(previewResult?.learnToRowConversion || pendingMembershipChange?.learnToRowConversion || (teamSettings?.learnToRow ? {
    ...teamSettings.learnToRow,
    isActive: true
  } : null)), [pendingMembershipChange?.learnToRowConversion, previewResult?.learnToRowConversion, teamSettings?.learnToRow]);
  const membershipPricing = useMemo(() => normalizeMembershipPricing(previewResult?.membershipPricing || pendingMembershipChange?.membershipPricing || null), [pendingMembershipChange?.membershipPricing, previewResult?.membershipPricing]);
  const lockedLearnToRowPricingTag = useMemo(() => requestedPlanType === 'individual' && learnToRowConversion?.pricingTagLocked && learnToRowConversion?.fixedMembershipPricingTag ? learnToRowConversion.fixedMembershipPricingTag : '', [learnToRowConversion?.fixedMembershipPricingTag, learnToRowConversion?.pricingTagLocked, requestedPlanType]);
  const effectiveRequestedPricingTagNames = useMemo(() => {
    if (requestedPlanType !== 'individual') return [];
    if (lockedLearnToRowPricingTag) return [lockedLearnToRowPricingTag];
    return flatRequestedPricingTagNames;
  }, [flatRequestedPricingTagNames, lockedLearnToRowPricingTag, requestedPlanType]);
  const learnToRowConversionSummary = useMemo(() => buildLearnToRowConversionCopy(learnToRowConversion, membershipCurrency), [learnToRowConversion, membershipCurrency]);
  const membershipPricingSummary = useMemo(() => buildMembershipPricingCopy(membershipPricing, membershipCurrency), [membershipCurrency, membershipPricing]);
  const learnToRowCurrentSetupLabel = useMemo(() => buildLearnToRowCurrentSetupLabel(learnToRowConversion), [learnToRowConversion]);
  const learnToRowConversionBlockedUntil = learnToRowConversion?.status === 'active_course'
    && learnToRowConversion?.conversionStartsAt instanceof Date
    && learnToRowConversion.conversionStartsAt.getTime() > Date.now()
    ? learnToRowConversion.conversionStartsAt
    : null;
  const learnToRowConversionBlockedLabel = formatDateTime(learnToRowConversionBlockedUntil);
  const currentPlanLabel = currentPlanType === 'family_tier' || currentMembership.membershipPlanLabel || currentMembership.membershipPlanSnapshot?.label || teamSettings?.paymentMode
    ? getMembershipPlanLabel(currentMembership)
    : learnToRowCurrentSetupLabel || 'No membership set up yet';
  const requiresSubscriptionRecovery = teamSettings?.paymentMode === 'subscription' && teamSettings?.subscriptionRecoveryRequired === true;
  const renewalManagedByPayer = ['family_covered', 'additional_covered'].includes(teamSettings?.paymentMode);
  const hasRenewingMembership = ['manual', 'subscription'].includes(teamSettings?.paymentMode)
    && !['cancelled', 'canceled', 'inactive', 'ended'].includes(teamSettings?.status)
    && !requiresSubscriptionRecovery;
  const renewalDate = hasRenewingMembership
    ? toDate(teamSettings?.membershipYearAnchorTarget) || toDate(teamSettings?.nextDueDate) || paidMembershipEnd
    : null;
  const renewalDateLabel = renewalDate?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const renewalSummary = <div className="membership-renewal-summary">
      <span className="membership-renewal-summary__label">Membership renewal</span>
      {renewalDate ? <time className="membership-renewal-summary__date" dateTime={renewalDate.toISOString()}>{renewalDateLabel}</time>
        : <span className="text-muted small">{renewalManagedByPayer ? 'Managed by the person paying for your membership.' : 'No renewal date scheduled yet.'}</span>}
    </div>;
  const currentCadenceLabel = cadenceFrequencyLabel(inferredStoredCadence || {
    unit: teamSettings?.interval,
    count: teamSettings?.intervalCount
  }, { capitalized: true });
  const currentFamilyId = typeof currentMembership.membershipCoverageFamilyId === 'string' ? currentMembership.membershipCoverageFamilyId.trim() : '';
  const currentTargetUserIds = useMemo(() => {
    const covered = Array.isArray(currentMembership.membershipCoveredMemberIds) ? currentMembership.membershipCoveredMemberIds : [];
    return normalizeRequestedTargetIds(covered, user?.uid || '');
  }, [currentMembership.membershipCoveredMemberIds, user?.uid]);
  const adminLabel = useMemo(() => {
    const names = (adminNames || []).filter(name => typeof name === 'string' && name.trim() !== '');
    if (names.length === 0) return 'the club admins';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names[0]}, ${names[1]} and ${names.length - 2} more admins`;
  }, [adminNames]);
  const activeFamilyGroup = useMemo(() => familyGroups.find(group => group.familyId === requestedFamilyId) || familyGroups[0] || null, [familyGroups, requestedFamilyId]);
  const selectedFamilyTier = useMemo(() => familyTierOptions.find(tier => tier.id === requestedPlanTierId) || familyTierOptions[0] || null, [familyTierOptions, requestedPlanTierId]);
  const parentOptions = useMemo(() => Array.isArray(activeFamilyGroup?.members) ? activeFamilyGroup.members.filter(member => member.role === 'parent') : [], [activeFamilyGroup]);
  const childOptions = useMemo(() => Array.isArray(activeFamilyGroup?.members) ? activeFamilyGroup.members.filter(member => member.role === 'child') : [], [activeFamilyGroup]);
  const selectedTargetSet = useMemo(() => new Set(normalizeRequestedTargetIds(requestedTargetUserIds, user?.uid || '')), [requestedTargetUserIds, user?.uid]);
  const requestedTargetDisplayNames = useMemo(() => {
    const selectedIds = normalizeRequestedTargetIds(requestedTargetUserIds, user?.uid || '');
    const namesById = new Map();
    familyGroups.forEach(group => {
      group.members.forEach(member => {
        namesById.set(member.uid, member.name || 'Family member');
      });
    });
    if (user?.uid && !namesById.has(user.uid)) {
      namesById.set(user.uid, memberName || user.displayName || user.email || 'You');
    }
    return selectedIds.map(uid => namesById.get(uid) || 'Family member');
  }, [familyGroups, memberName, requestedTargetUserIds, user?.displayName, user?.email, user?.uid]);
  useEffect(() => {
    if (!teamId || !user?.uid) {
      setActiveRequest(null);
      return undefined;
    }
    const requestRef = doc(db, 'teams', teamId, 'membershipRequests', user.uid);
    const unsub = safeOnSnapshot(requestRef, snap => {
      const data = snap.exists() ? snap.data() || {} : null;
      setActiveRequest(data ? {
        id: snap.id,
        ...data
      } : null);
    }, undefined, { label: 'membership-active-request', teamId });
    return () => unsub();
  }, [teamId, user?.uid]);
  useEffect(() => {
    setMemberRecord(null);
    if (!teamId || !user?.uid) return undefined;
    return safeOnSnapshot(doc(db, 'teams', teamId, 'members', user.uid), snap => {
      setMemberRecord(snap.exists() ? snap.data() || {} : null);
    }, () => setMemberRecord(null), { label: 'membership-member-coverage', teamId });
  }, [teamId, user?.uid]);
  useEffect(() => {
    if (!teamId) return undefined;
    const setupRef = doc(db, 'teams', teamId, 'stripe', 'setup');
    const unsub = safeOnSnapshot(setupRef, snap => {
      setStripeSetup(snap.exists() ? snap.data() || {} : null);
    }, undefined, { label: 'membership-stripe-setup', teamId, suppressPermissionDenied: true });
    return () => unsub();
  }, [teamId]);
  useEffect(() => {
    if (!teamId || !user?.uid) {
      setTeamSettings(null);
      return undefined;
    }
    const settingsRef = doc(db, 'users', user.uid, 'teamSettings', teamId);
    const unsub = safeOnSnapshot(settingsRef, snap => {
      setTeamSettings(snap.exists() ? snap.data() || {} : null);
    }, undefined, { label: 'membership-team-settings', teamId, suppressPermissionDenied: true });
    return () => unsub();
  }, [teamId, user?.uid]);
  useEffect(() => {
    setPaidMembershipEnd(null);
    if (!teamId || !user?.uid) {
      setInferredStoredCadence(null);
      return undefined;
    }
    let cancelled = false;
    async function loadInferredCadence() {
      try {
        const paymentsSnap = await getDocs(query(collection(db, 'users', user.uid, 'payments'), where('teamId', '==', teamId)));
        if (cancelled) return;
        const paidEnds = paymentsSnap.docs.map(docSnap => docSnap.data() || {})
          .filter(payment => (!payment.paymentType || payment.paymentType === 'membership') && payment.paid !== false && payment.paidAt)
          .map(payment => toDate(payment.periodEnd)).filter(Boolean);
        setPaidMembershipEnd(paidEnds.sort((a, b) => b.getTime() - a.getTime())[0] || null);
        const inferred = paymentsSnap.docs.map(docSnap => docSnap.data() || {}).filter(payment => {
          const paymentType = String(payment?.paymentType || '').toLowerCase();
          return !paymentType || paymentType === 'membership';
        }).sort((a, b) => {
          const aMarker = (typeof a?.paidAt?.toDate === 'function' ? a.paidAt.toDate() : a?.paidAt ? new Date(a.paidAt) : null) || (typeof a?.periodEnd?.toDate === 'function' ? a.periodEnd.toDate() : a?.periodEnd ? new Date(a.periodEnd) : null) || (typeof a?.createdAt?.toDate === 'function' ? a.createdAt.toDate() : a?.createdAt ? new Date(a.createdAt) : null);
          const bMarker = (typeof b?.paidAt?.toDate === 'function' ? b.paidAt.toDate() : b?.paidAt ? new Date(b.paidAt) : null) || (typeof b?.periodEnd?.toDate === 'function' ? b.periodEnd.toDate() : b?.periodEnd ? new Date(b.periodEnd) : null) || (typeof b?.createdAt?.toDate === 'function' ? b.createdAt.toDate() : b?.createdAt ? new Date(b.createdAt) : null);
          return (bMarker?.getTime?.() || 0) - (aMarker?.getTime?.() || 0);
        }).map(payment => extractCadenceFromPayment(payment, stripeSetup || {})).find(Boolean) || null;
        setInferredStoredCadence(inferred);
      } catch {
        if (!cancelled) setInferredStoredCadence(null);
      }
    }
    loadInferredCadence();
    return () => {
      cancelled = true;
    };
  }, [stripeSetup, teamId, user?.uid]);
  useEffect(() => {
    if (!user?.uid || familyTierOptions.length === 0) {
      setFamilyGroups([]);
      setFamilySelectionLoading(false);
      return undefined;
    }
    let cancelled = false;
    async function loadFamilyGroups() {
      setFamilySelectionLoading(true);
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const familyRoles = userSnap.exists() ? userSnap.data()?.family_roles || {} : {};
        const parentFamilyIds = Object.entries(familyRoles).filter(([, role]) => role === 'parent').map(([familyId]) => familyId).filter(familyId => typeof familyId === 'string' && familyId.trim() !== '');
        if (parentFamilyIds.length === 0) {
          if (!cancelled) setFamilyGroups([]);
          return;
        }
        const groups = [];
        for (const familyId of parentFamilyIds) {
          const [familySnap, membersSnap] = await Promise.all([getDoc(doc(db, 'families', familyId)), getDocs(collection(db, 'families', familyId, 'members'))]);
          if (!familySnap.exists()) continue;
          const rawMembers = membersSnap.docs.map(memberDoc => ({
            uid: memberDoc.id,
            role: String(memberDoc.data()?.role || '').toLowerCase()
          })).filter(member => member.role === 'parent' || member.role === 'child');
          if (rawMembers.length === 0) continue;
          const members = await Promise.all(rawMembers.map(async member => {
            const profileSnap = await getDoc(doc(db, 'users', member.uid));
            const profile = profileSnap.exists() ? profileSnap.data() || {} : {};
            return {
              uid: member.uid,
              role: member.role,
              isSelf: member.uid === user.uid,
              name: formatFamilyMemberName(profile)
            };
          }));
          groups.push({
            familyId,
            familyName: familySnap.data()?.name || 'Family',
            members: members.sort((a, b) => {
              if (a.isSelf && !b.isSelf) return -1;
              if (!a.isSelf && b.isSelf) return 1;
              if (a.role !== b.role) return a.role.localeCompare(b.role);
              return a.name.localeCompare(b.name);
            })
          });
        }
        if (cancelled) return;
        const sortedGroups = groups.sort((a, b) => {
          const nameCompare = String(a.familyName || '').localeCompare(String(b.familyName || ''));
          if (nameCompare !== 0) return nameCompare;
          return String(a.familyId || '').localeCompare(String(b.familyId || ''));
        });
        setFamilyGroups(sortedGroups);
      } catch (err) {
        if (!cancelled) setFamilyGroups([]);
      } finally {
        if (!cancelled) setFamilySelectionLoading(false);
      }
    }
    loadFamilyGroups();
    return () => {
      cancelled = true;
    };
  }, [familyTierOptions.length, user?.uid]);
  const openRequestModal = useCallback(() => {
    const initialPlanType = normalizeMembershipPlanType(activeRequest?.requestedPlanType || currentPlanType);
    const initialTierId = (
      (typeof activeRequest?.requestedPlanTierId === 'string' && activeRequest.requestedPlanTierId.trim())
      || (typeof currentMembership.membershipPlanTierId === 'string' && currentMembership.membershipPlanTierId.trim())
      || familyTierOptions[0]?.id
      || ''
    );
    const initialFamilyId = (
      (typeof activeRequest?.requestedFamilyId === 'string' && activeRequest.requestedFamilyId.trim())
      || currentFamilyId
      || familyGroups[0]?.familyId
      || ''
    );
    const initialTargetIds = normalizeRequestedTargetIds(activeRequest?.requestedTargetUserIds || currentTargetUserIds, user?.uid || '');
    const initialRequestedPricingTagNames = Array.from(new Set([typeof activeRequest?.requestedPricingTag === 'string' ? activeRequest.requestedPricingTag.trim() : '', ...(Array.isArray(activeRequest?.requestedAdminTagNames) ? activeRequest.requestedAdminTagNames : [])].map(tagName => typeof tagName === 'string' ? tagName.trim() : '').filter(Boolean)));
    const fallbackPricingTagNames = currentAdminManagedTagNames.filter(tagName => pricingTagOptions.some(tag => tag?.name === tagName));
    setRequestedPlanType(initialPlanType);
    setRequestedPlanTierId(initialTierId);
    setRequestedPricingTagsByCategory(groupSelectedTagsByCategory(pricingTagGroups, initialRequestedPricingTagNames.length > 0 ? initialRequestedPricingTagNames : fallbackPricingTagNames));
    setRequestedFamilyId(initialFamilyId);
    setRequestedTargetUserIds(initialPlanType === 'family_tier' ? initialTargetIds : normalizeRequestedTargetIds([user?.uid || ''], user?.uid || ''));
    setRequestNote(typeof activeRequest?.note === 'string' && activeRequest.note.trim() !== '' ? activeRequest.note : '');
    setRequestedCollectionRoute(
      activeRequest?.requestedMembershipCollectionRoute
      || teamSettings?.pendingMembershipCollectionRouteChange?.targetRoute
      || teamSettings?.membershipCollectionRoute
      || (teamSettings?.paymentMode === 'subscription' ? 'stripe' : 'offline')
    );
    const activeRequestTargetCadence = activeRequest?.preview?.targetConfig?.cadence || null;
    const activeRequestCurrentCadence = activeRequest?.preview?.currentConfig?.cadence || null;
    const pendingTargetCadence = pendingMembershipChange?.targetConfig?.cadence || null;
    const pendingCurrentCadence = pendingMembershipChange?.currentConfigSnapshot?.cadence || null;
    const storedCadence = {
      unit: inferredStoredCadence?.unit || teamSettings?.interval,
      count: inferredStoredCadence?.count || teamSettings?.intervalCount,
      discountPct: inferredStoredCadence?.discountPct || 0
    };
    const fallbackCadence = findCadenceOption(cadenceOptions, activeRequestTargetCadence) || findCadenceOption(cadenceOptions, activeRequestCurrentCadence) || findCadenceOption(cadenceOptions, pendingTargetCadence) || findCadenceOption(cadenceOptions, pendingCurrentCadence) || findCadenceOption(cadenceOptions, storedCadence) || cadenceOptions.find(option => option.key === 'yearly') || cadenceOptions[0] || null;
    setSelectedCadenceKey(fallbackCadence?.key || '');
    setRequestError('');
    setPreviewResult(null);
    setRequestModalOpen(true);
  }, [activeRequest, cadenceOptions, currentAdminManagedTagNames, currentFamilyId, currentPlanType, currentTargetUserIds, familyGroups, familyTierOptions, inferredStoredCadence?.count, inferredStoredCadence?.discountPct, inferredStoredCadence?.unit, pendingMembershipChange?.currentConfigSnapshot?.cadence, pendingMembershipChange?.targetConfig?.cadence, pricingTagGroups, pricingTagOptions, teamSettings?.interval, teamSettings?.intervalCount, teamSettings?.membershipCollectionRoute, currentMembership.membershipPlanTierId, teamSettings?.paymentMode, teamSettings?.pendingMembershipCollectionRouteChange?.targetRoute, user?.uid]);
  useEffect(() => {
    if (requestedPlanType !== 'family_tier') {
      setRequestedTargetUserIds(normalizeRequestedTargetIds([user?.uid || ''], user?.uid || ''));
      return;
    }
    setRequestedTargetUserIds(prev => {
      const normalized = normalizeRequestedTargetIds(prev, user?.uid || '');
      const allowed = new Set((activeFamilyGroup?.members || []).map(member => member.uid));
      const filtered = normalized.filter(uid => uid === user?.uid || allowed.has(uid));
      return normalizeRequestedTargetIds(filtered, user?.uid || '');
    });
  }, [activeFamilyGroup, requestedPlanType, user?.uid]);
  const toggleRequestedTarget = memberUid => {
    const uid = typeof memberUid === 'string' ? memberUid.trim() : '';
    if (!uid || uid === user?.uid) return;
    setRequestedTargetUserIds(prev => {
      const next = new Set(normalizeRequestedTargetIds(prev, user?.uid || ''));
      if (next.has(uid)) next.delete(uid);else next.add(uid);
      return normalizeRequestedTargetIds(Array.from(next), user?.uid || '');
    });
  };
  const toggleRequestedPricingTag = (category, tagName, multiAssignable) => {
    setRequestedPricingTagsByCategory(prev => {
      const existing = Array.isArray(prev[category]) ? prev[category] : [];
      const isSelected = existing.includes(tagName);
      const nextForCategory = multiAssignable ? isSelected ? existing.filter(name => name !== tagName) : [...existing, tagName].sort((a, b) => a.localeCompare(b)) : isSelected ? [] : [tagName];
      return {
        ...prev,
        [category]: nextForCategory
      };
    });
  };
  const selectedParentCount = useMemo(() => parentOptions.filter(member => member.uid !== user?.uid && selectedTargetSet.has(member.uid)).length, [parentOptions, selectedTargetSet, user?.uid]);
  const selectedChildCount = useMemo(() => childOptions.filter(member => selectedTargetSet.has(member.uid)).length, [childOptions, selectedTargetSet]);
  const buildRequestData = useCallback(() => {
    const requestedTargetIds = requestedPlanType === 'family_tier' ? normalizeRequestedTargetIds(requestedTargetUserIds, user?.uid || '') : [user?.uid || ''];
    const selectedPricingTagNames = requestedPlanType === 'individual' ? effectiveRequestedPricingTagNames : [];
    const primaryPricingTag = selectedPricingTagNames[0] || '';
    return {
      membershipPlanType: requestedPlanType,
      membershipPlanTierId: requestedPlanType === 'family_tier' ? selectedFamilyTier?.id || requestedPlanTierId || '' : '',
      membershipTargetFamilyId: requestedPlanType === 'family_tier' ? activeFamilyGroup?.familyId || requestedFamilyId || '' : '',
      membershipTargetUserIds: requestedTargetIds,
      membershipPricingTag: primaryPricingTag,
      membershipDiscountCode: membershipDiscountCode.trim().toUpperCase(),
      ...(membershipMode === 'hybrid' && requestedCollectionRoute
        ? { membershipCollectionRoute: requestedCollectionRoute }
        : {}),
      membershipAdminTagNames: primaryPricingTag ? selectedPricingTagNames.filter(tagName => tagName !== primaryPricingTag) : selectedPricingTagNames,
      ...(selectedCadence ? {
        selectedCadence: {
          unit: selectedCadence.unit,
          count: selectedCadence.count,
          discountPct: selectedCadence.discountPct || 0,
          key: selectedCadence.key
        }
      } : {})
    };
  }, [activeFamilyGroup?.familyId, effectiveRequestedPricingTagNames, membershipDiscountCode, membershipMode, requestedCollectionRoute, requestedFamilyId, requestedPlanTierId, requestedPlanType, requestedTargetUserIds, selectedCadence, selectedFamilyTier?.id, user?.uid]);
  const requestPreview = useMemo(() => summarizeMembershipRequest({
    requestedPlanLabel: requestedPlanType === 'family_tier' ? selectedFamilyTier?.label || 'Family Membership' : 'Individual Membership',
    requestedPricingTag: requestedPlanType === 'individual' ? effectiveRequestedPricingTagNames[0] || '' : '',
    requestedAdminTagNames: requestedPlanType === 'individual' ? effectiveRequestedPricingTagNames.slice(1) : [],
    requestedTargetDisplayNames: requestedPlanType === 'family_tier' ? requestedTargetDisplayNames : [],
    requestedFamilyName: requestedPlanType === 'family_tier' ? activeFamilyGroup?.familyName || '' : '',
    note: requestNote.trim()
  }), [activeFamilyGroup?.familyName, effectiveRequestedPricingTagNames, requestNote, requestedPlanType, requestedTargetDisplayNames, selectedFamilyTier?.label]);
  const previewSummary = useMemo(() => buildMembershipPreviewCopy(previewResult, membershipCurrency) || previewResult?.summary || requestPreview || 'Choose the membership changes you want to make.', [membershipCurrency, previewResult, requestPreview]);
  const isInitialLearnToRowSetup = Boolean(
    previewResult?.initialMembershipSetup
    && !teamSettings?.paymentMode
    && learnToRowConversion?.isActive
  );
  const submitButtonLabel = useMemo(() => {
    if (isInitialLearnToRowSetup && previewResult?.effectiveMode === 'immediate_after_payment') {
      return 'Pay Manually';
    }
    if (isInitialLearnToRowSetup) {
      return 'Start Manual Membership';
    }
    return getSubmitButtonLabel(previewResult, activeRequest);
  }, [activeRequest, isInitialLearnToRowSetup, previewResult]);
  useEffect(() => {
    let cancelled = false;
    async function loadPreview() {
      if (!requestModalOpen || !teamId || !user?.uid) {
        if (!cancelled) {
          setPreviewResult(null);
          setPreviewLoading(false);
        }
        return;
      }
      if (requestedPlanType === 'family_tier' && familyTierOptions.length > 0 && !selectedFamilyTier) {
        setPreviewResult(null);
        setPreviewLoading(false);
        return;
      }
      if (requestedPlanType === 'family_tier' && familyGroups.length > 0 && !activeFamilyGroup) {
        setPreviewResult(null);
        setPreviewLoading(false);
        return;
      }
      setPreviewLoading(true);
      try {
        const token = await user.getIdToken(true);
        const res = await fetch(getFunctionUrl('previewMembershipChange'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            teamId,
            requestData: buildRequestData()
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.error || 'Could not preview membership change.');
        }
        if (!cancelled) {
          setRequestError('');
          setPreviewResult(data);
        }
      } catch (err) {
        if (!cancelled) {
          setPreviewResult(null);
          setRequestError(err?.message || 'Could not preview membership change.');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [activeFamilyGroup, buildRequestData, familyGroups.length, familyTierOptions.length, requestModalOpen, requestedPlanType, selectedFamilyTier, teamId, user]);
  const handleSubmitMembershipChange = async () => {
    if (!teamId || !user?.uid) {
      setRequestError('You must be signed in to manage membership.');
      return;
    }
    if (requestedPlanType === 'family_tier') {
      if (!selectedFamilyTier) {
        setRequestError('Choose a family membership tier.');
        return;
      }
      if (!activeFamilyGroup) {
        setRequestError('Add or link a family in your profile before changing to a family membership.');
        return;
      }
      if (selectedParentCount > Math.max(0, Number(selectedFamilyTier.maxParents || 1) - 1)) {
        setRequestError('Too many extra parents selected for this tier.');
        return;
      }
      if (selectedChildCount > Math.max(0, Number(selectedFamilyTier.maxChildren || 0))) {
        setRequestError('Too many children selected for this tier.');
        return;
      }
    }
    if (requestedPlanType === 'individual') {
      const missingRequiredPricing = getMissingRequiredTagGroups(requiredPricingTagGroups, effectiveRequestedPricingTagNames, {
        includePricing: true
      });
      if (missingRequiredPricing.length > 0) {
        setRequestError(`Choose a required pricing tag for ${missingRequiredPricing[0].category}.`);
        return;
      }
    }
    setRequestSubmitting(true);
    setRequestError('');
    setRequestStatus('');
    try {
      const payload = {
        teamId,
        requestData: buildRequestData(),
        note: requestNote.trim()
      };
      const token = await user.getIdToken(true);
      const res = await fetch(getFunctionUrl('submitMembershipChange'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Could not save this membership change.');
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      if (data?.status === 'pending_approval') {
        setRequestStatus(activeRequest?.status === 'requested' ? 'Request updated.' : 'Request sent to admins.');
      } else if (data?.status === 'payment_required') {
        setRequestStatus('Payment required to activate this change.');
      } else if (data?.status === 'scheduled_for_renewal' || data?.status === 'applied_and_scheduled') {
        const transition = getMembershipCadenceTransition(data?.pendingMembershipChange || data?.preview || {});
        setRequestStatus(transition.confirmedWithStripe && transition.targetLabel && transition.effectiveDate
          ? `${transition.targetLabel} billing confirmed with Stripe for ${transition.effectiveDate}. ${transition.currentLabel || 'Your current billing'} remains active until then.`
          : 'Membership change scheduled for the next renewal.');
      } else if (data?.status === 'scheduled_change_removed') {
        setRequestStatus('The scheduled billing change was removed from Stripe. Your current billing cadence will continue.');
      } else {
        setRequestStatus('Membership updated.');
      }
      if (Object.prototype.hasOwnProperty.call(data || {}, 'pendingMembershipChange')) {
        setTeamSettings(previous => previous ? {
          ...previous,
          pendingMembershipChange: data.pendingMembershipChange || null,
        } : previous);
      }
      setRequestModalOpen(false);
    } catch (err) {
      setRequestError(err?.message || 'Unable to save this membership change right now.');
    } finally {
      setRequestSubmitting(false);
    }
  };
  const handleStartRecurringMembership = async () => {
    if (!teamId || !user?.uid) {
      setRequestError('You must be signed in to manage membership.');
      return;
    }
    setRequestSubmitting(true);
    setRequestError('');
    setRequestStatus('');
    try {
      const token = await user.getIdToken(true);
      const res = await fetch(getFunctionUrl('createCustomerAndSubscription'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId,
          membershipPlanType: requestedPlanType,
          membershipPlanTierId: requestedPlanType === 'family_tier' ? selectedFamilyTier?.id || requestedPlanTierId || null : null,
          membershipTargetUserIds: requestedPlanType === 'family_tier' ? normalizeRequestedTargetIds(requestedTargetUserIds, user?.uid || '') : [user?.uid || ''],
          membershipTargetFamilyId: requestedPlanType === 'family_tier' ? activeFamilyGroup?.familyId || requestedFamilyId || null : null,
          membershipPricingTag: requestedPlanType === 'individual' ? effectiveRequestedPricingTagNames[0] || '' : '',
          membershipDiscountCode: membershipDiscountCode.trim().toUpperCase(),
          selectedCadence: selectedCadence ? {
            unit: selectedCadence.unit,
            count: selectedCadence.count,
            discountPct: selectedCadence.discountPct || 0,
            key: selectedCadence.key
          } : null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Could not start subscription setup.');
      }
      if (data?.clientSecret || data?.setupIntentClientSecret) {
        const checkoutParams = new URLSearchParams({
          clientSecret: data?.clientSecret || '',
          setupIntentClientSecret: data?.setupIntentClientSecret || '',
          subscriptionId: data?.subscriptionId || '',
          paymentIntentId: data?.paymentIntentId || '',
          setupIntentId: data?.setupIntentId || '',
          requiresSubscriptionFinalization: String(data?.requiresSubscriptionFinalization || false),
          firstChargeBaseAmount: String(data?.firstChargeBaseAmount ?? ''),
          firstChargePlatformFee: String(data?.firstChargePlatformFee ?? ''),
          firstChargeStripeFee: String(data?.firstChargeStripeFee ?? ''),
          team: teamId || '',
          currency: membershipCurrency || '',
          baseAmount: String(data?.baseAmount ?? ''),
          platformFee: String(data?.platformFee ?? ''),
          stripeFee: String(data?.stripeFee ?? ''),
          finalAmount: String(data?.finalAmount ?? ''),
          firstChargeAmount: String(data?.firstChargeAmount ?? ''),
          membershipYearAnchor: data?.membershipYearAnchor || '',
          learnToRowCreditAppliedMinor: String(data?.learnToRowCreditAppliedMinor ?? ''),
          membershipDiscountAppliedMinor: String(data?.membershipDiscountAppliedMinor ?? ''),
          membershipStoredCreditAppliedMinor: String(data?.membershipStoredCreditAppliedMinor ?? ''),
          membershipStoredCreditRemainingMinor: String(data?.membershipStoredCreditRemainingMinor ?? '')
        });
        window.location.href = `/subscriptionCheckout?${checkoutParams.toString()}`;
        return;
      }
      setRequestStatus('Subscription created.');
      setRequestModalOpen(false);
    } catch (err) {
      setRequestError(err?.message || 'Could not start subscription setup.');
    } finally {
      setRequestSubmitting(false);
    }
  };
  const currentCoverageSummary = useMemo(() => {
    if (!teamSettings?.paymentMode && learnToRowCurrentSetupLabel) {
      if (learnToRowConversionBlockedLabel) {
        return `Your course place is active. Membership conversion opens on ${learnToRowConversionBlockedLabel}.`;
      }
      return 'Your course place is active. No extra membership is required for this Learn to Row place.';
    }
    if (!teamSettings?.paymentMode && currentPlanType !== 'family_tier') return 'Set up your own membership here.';
    if (currentPlanType !== 'family_tier') return 'Just your own membership.';
    if (Array.isArray(currentMembership.membershipCoveredMemberIds) && currentMembership.membershipCoveredMemberIds.length > 0) {
      return `${currentMembership.membershipCoveredMemberIds.length} member${currentMembership.membershipCoveredMemberIds.length === 1 ? '' : 's'} covered by this family plan.`;
    }
    if (teamSettings?.paymentMode === 'family_covered') {
      return 'You are currently covered by a family membership.';
    }
    return 'Family membership settings are active.';
  }, [currentPlanType, learnToRowConversionBlockedLabel, learnToRowCurrentSetupLabel, currentMembership.membershipCoveredMemberIds, teamSettings?.paymentMode]);
  const handleOpenBillingPortal = async () => {
    if (!user) return;
    setPortalLoading(true);
    setRequestError('');
    try {
      const token = await user.getIdToken(true);
      const res = await fetch(getFunctionUrl('createStripePortalSession'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId,
          currency: stripeSetup?.currency || teamSettings?.currency || ''
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || 'Could not open billing portal.');
      }
      window.location.href = data.url;
    } catch (err) {
      setRequestError(err?.message || 'Could not open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };
  const handlePayPendingMembershipChange = async () => {
    if (!user?.uid || !teamId) return;
    setPortalLoading(true);
    setRequestError('');
    try {
      const token = await user.getIdToken(true);
      const res = await fetch(getFunctionUrl('payPendingMembershipChange'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || 'Could not start payment for this membership change.');
      }
      window.location.href = data.url;
    } catch (err) {
      setRequestError(err?.message || 'Could not start payment for this membership change.');
    } finally {
      setPortalLoading(false);
    }
  };
  const handleCancelPendingMembershipChange = async () => {
    if (!user?.uid || !teamId) return;
    if (!window.confirm('Discard this pending membership change? Any reserved or scheduled change will be removed.')) return;
    setCancelPendingLoading(true);
    setRequestError('');
    try {
      const token = await user.getIdToken(true);
      const res = await fetch(getFunctionUrl('cancelPendingMembershipChange'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Could not cancel this membership change.');
      }
      setTeamSettings(prev => prev ? {
        ...prev,
        pendingMembershipChange: Object.prototype.hasOwnProperty.call(data || {}, 'pendingMembershipChange')
          ? data.pendingMembershipChange
          : null
      } : prev);
      if (pendingMembershipStatus === 'pending_approval') {
        setActiveRequest(null);
      }
      setRequestStatus(pendingMembershipStatus === 'pending_approval'
        ? 'Membership request cancelled.'
        : pendingMembershipStatus === 'scheduled_for_renewal'
          ? data?.clubBillingTransitionPreserved
            ? 'Your membership change was discarded. The club billing-policy change remains scheduled.'
            : 'The scheduled billing change was removed from Stripe.'
          : 'Pending membership change discarded.');
      setPreviewResult(null);
      setRequestModalOpen(false);
    } catch (err) {
      setRequestError(err?.message || 'Could not cancel this membership change.');
    } finally {
      setCancelPendingLoading(false);
    }
  };
  const handleLeaveClick = () => {
    if (selfManaged) {
      setLeaveStatus('You cannot leave the club while you still have admin access. Remove your admin role first.');
      return;
    }
    setLeaveStatus('');
    onLeave?.();
  };
  useEffect(() => {
    if (!autoOpen || requestModalOpen) return;
    openRequestModal();
    onAutoOpenHandled?.();
  }, [autoOpen, requestModalOpen, openRequestModal, onAutoOpenHandled]);
  return <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">Manage my Membership</span>
      </div>

      <div className="card-body">
        <MembershipPaymentStatus teamId={teamId} enabled={reconciliationEnabled} />
        {teamSettings?.hostedStripeSetupRequiredAt && !teamSettings?.hostedStripeSetupCompletedAt && <div className="membership-request-banner" role="alert" style={{ marginBottom: '0.9rem' }}><strong>Membership payment setup required</strong><p className="text-muted" style={{ margin: '0.35rem 0 0.65rem' }}>{teamSettings.pendingMembershipCollectionRouteChange?.targetRoute === 'stripe' ? 'Your move to online renewal payments has been approved. Save a card for future payments; your current paid membership stays active.' : 'Your club now collects membership payments through Stripe. Review your membership to set up payment.'}</p><a className="btn btn--primary btn--sm" href={`/join/${teamId}?manage=1`}>Set up membership payment</a></div>}
        {teamSettings?.pendingMembershipCollectionRouteChange?.targetRoute === 'stripe' && teamSettings.pendingMembershipCollectionRouteChange.status === 'scheduled_for_renewal' && teamSettings.pendingMembershipCollectionRouteChange.paymentSetupCompleted && <div className="membership-request-banner" role="status" style={{ marginBottom: '0.9rem' }}><strong>Online renewal payments set up</strong><p className="text-muted" style={{ margin: '0.35rem 0 0' }}>Your card is saved. Your current offline coverage stays active until {formatDateTime(toDate(teamSettings.pendingMembershipCollectionRouteChange.effectiveAt)) || 'the renewal boundary'}, when online renewal payments begin.</p></div>}
        {teamSettings?.pendingMembershipCollectionRouteChange?.status === 'pending_approval' && <div className="membership-request-banner" role="status" style={{ marginBottom: '0.9rem' }}><strong>Payment route request awaiting approval</strong><p className="text-muted" style={{ margin: '0.35rem 0 0' }}>Your current payment arrangement stays active until an administrator approves this request.</p></div>}
        {teamSettings?.pendingMembershipCollectionRouteChange?.targetRoute === 'offline' && teamSettings.pendingMembershipCollectionRouteChange.status === 'scheduled_for_renewal' && <div className="membership-request-banner" role="status" style={{ marginBottom: '0.9rem' }}><strong>Offline payment scheduled</strong><p className="text-muted" style={{ margin: '0.35rem 0 0' }}>Your current online coverage stays active until {formatDateTime(toDate(teamSettings.pendingMembershipCollectionRouteChange.effectiveAt)) || 'the renewal boundary'}. The club will then confirm future payments offline.</p></div>}
        <p className="text-muted" style={{
        marginBottom: '0.75rem'
      }}>
          Current setup: <strong>{currentPlanLabel}</strong>{currentCadenceLabel ? <> — <strong>{currentCadenceLabel} billing</strong></> : null}{membershipMode === 'hybrid' ? <> — <strong>{(teamSettings?.membershipCollectionRoute || (teamSettings?.paymentMode === 'subscription' ? 'stripe' : 'offline')) === 'stripe' ? 'online payment' : 'offline payment'}</strong></> : null}. {currentCoverageSummary}
        </p>
        {renewalSummary}

        <p className="text-muted" style={{
        marginBottom: '0.75rem'
      }}>
          <strong>Admin-managed tags:</strong>{' '}
          {currentAdminManagedTagNames.length > 0 ? currentAdminManagedTagNames.join(', ') : 'None assigned.'}
        </p>

        {pendingBillingTransition && <div className={`membership-change-card membership-change-card--${pendingBillingTransition.status || 'pending'}`}>
            <strong>{displayedBillingPolicyTransition.preparing ? 'Club billing change is being prepared' : displayedBillingPolicyTransition.manualReview ? 'Billing change needs club review' : displayedBillingPolicyTransition.changed ? 'Upcoming club billing change' : 'Club billing options updated'}</strong>
            {displayedBillingPolicyTransition.timeline && <p className="membership-change-card__timeline">{displayedBillingPolicyTransition.timeline}</p>}
            {!displayedBillingPolicyTransition.changed && <p className="membership-change-card__timeline">{displayedBillingPolicyTransition.currentLabel} billing continues. Flexible billing choice is now available.</p>}
            {displayedBillingPolicyTransition.changed && <p style={{ margin: '0.4rem 0 0' }}>Next {cadenceFrequencyLabel(displayedBillingPolicyTransition.targetCadence)} renewal: {formatMinorAmount(pendingBillingTransition.targetAmountMinor || 0, pendingBillingTransition.currency || membershipCurrency)}.</p>}
            {!displayedBillingPolicyTransition.changed && !displayedBillingPolicyTransition.hasCadenceConflict && <p style={{ margin: '0.4rem 0 0' }}>Next renewal: {formatMinorAmount(pendingBillingTransition.targetAmountMinor || 0, pendingBillingTransition.currency || membershipCurrency)}.</p>}
            {billingPolicyTransition.preparing
              ? <p className="text-muted" style={{ margin: '0.5rem 0 0' }}>Your current billing remains unchanged while the club confirms this transition.</p>
              : billingPolicyTransition.manualReview
              ? <p className="text-warning" style={{ margin: '0.5rem 0 0' }}>Your current billing remains unchanged while the club resolves this with Stripe.</p>
              : <p className="membership-change-card__confirmed">{billingPolicyTransition.confirmedByClub ? 'Confirmed by club for your next manual renewal' : billingPolicyTransition.confirmedWithStripe ? 'Confirmed with Stripe' : 'Confirmed'}</p>}
            {pendingBillingTransition.memberControls === 'replaceable' && !billingPolicyTransition.manualReview && !billingPolicyTransition.preparing && <div style={{ marginTop: '0.75rem' }}>
                <button type="button" className="btn btn--primary btn--sm" onClick={openRequestModal}>Choose a Different Frequency</button>
              </div>}
          </div>}

        {pendingMembershipChange && <div className="membership-change-card">
            <strong>
              {pendingMembershipStatus === 'approved_pending_payment' || pendingMembershipStatus === 'payment_in_progress'
                ? 'Payment pending'
                : pendingMembershipStatus === 'scheduled_for_renewal'
                  ? pendingCadenceTransition.confirmedWithStripe ? 'Billing change confirmed' : 'Scheduled change — awaiting Stripe confirmation'
                  : 'Membership change in progress'}
            </strong>
            {cadenceOverriddenByClub && <p className="text-muted" style={{ margin: '0.5rem 0 0' }}>The club billing policy now controls the billing frequency. Your other requested membership changes are still preserved.</p>}
            {pendingMembershipStatus === 'scheduled_for_renewal' && !cadenceOverriddenByClub && pendingCadenceTransition.timeline && <p className="membership-change-card__timeline">
                {pendingCadenceTransition.timeline}
              </p>}
            {!pendingCadenceTransition.timeline && <p style={{
          margin: '0.5rem 0 0'
        }}>
              {pendingMembershipSummary}
            </p>}
            {pendingCadenceTransition.timeline && <p style={{ margin: '0.4rem 0 0' }}>
                Next {cadenceFrequencyLabel(pendingCadenceTransition.targetCadence)} renewal: {formatMinorAmount(pendingMembershipChange?.nextRenewalAmountMinor || 0, membershipCurrency)}.
              </p>}
            {pendingMembershipStatus === 'scheduled_for_renewal' && pendingCadenceTransition.confirmedWithStripe && <p className="membership-change-card__confirmed">
                Confirmed with Stripe
              </p>}
            {((pendingMembershipStatus === 'approved_pending_payment' || pendingMembershipStatus === 'payment_in_progress') || canCancelPendingMembershipChange) && <div style={{
          marginTop: '0.75rem'
        }}>
                <div style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap'
            }}>
                  {(pendingMembershipStatus === 'approved_pending_payment' || pendingMembershipStatus === 'payment_in_progress') && <button type="button" className="btn btn--primary btn--sm" onClick={handlePayPendingMembershipChange} disabled={portalLoading || cancelPendingLoading}>
                      {portalLoading ? 'Opening Payment...' : 'Pay to Activate'}
                    </button>}
                  {canCancelPendingMembershipChange && <button type="button" className="btn btn--secondary btn--sm" onClick={handleCancelPendingMembershipChange} disabled={cancelPendingLoading || portalLoading}>
                      {cancelPendingLoading ? 'Clearing...' : pendingMembershipStatus === 'pending_approval' ? 'Cancel Request' : pendingMembershipStatus === 'scheduled_for_renewal' ? cadenceOverriddenByClub ? 'Discard Member Change' : 'Remove Scheduled Change' : 'Discard Change'}
                    </button>}
                  {pendingMembershipStatus === 'scheduled_for_renewal' && !cadenceOverriddenByClub && <button type="button" className="btn btn--primary btn--sm" onClick={openRequestModal} disabled={cancelPendingLoading || portalLoading}>
                      Replace Scheduled Option
                    </button>}
                </div>
              </div>}
          </div>}

        {!selfManaged && activeRequest?.status === 'requested' && <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '0.75rem',
        background: '#f8fafc',
        marginBottom: '0.75rem'
      }}>
            <strong>Pending request</strong>
            <p style={{
          margin: '0.5rem 0 0'
        }}>
              {activeRequest.message || 'No details provided.'}
            </p>
            {formatTimestamp(activeRequest.updatedAt || activeRequest.createdAt) && <p className="text-muted small" style={{
          margin: '0.5rem 0 0'
        }}>
                Last updated {formatTimestamp(activeRequest.updatedAt || activeRequest.createdAt)}.
              </p>}
          </div>}

        {requiresSubscriptionRecovery && <div className="membership-request-banner" role="alert" style={{ marginBottom: '0.9rem' }}>
            <strong>Recurring payment needs to be restarted</strong>
            <p className="text-muted" style={{ margin: '0.35rem 0 0.65rem' }}>
              Stripe ended the previous subscription after payment failed. Your club access remains available during the non-payment grace period.
            </p>
            <button type="button" className="btn btn--primary btn--sm" onClick={handleStartRecurringMembership} disabled={requestSubmitting}>
              {requestSubmitting ? 'Starting…' : 'Restart Recurring Payment'}
            </button>
          </div>}

        {teamSettings?.paymentMode !== 'additional_covered' && (selfManaged ? <>
            <p className="text-muted" style={{
          marginBottom: '0.75rem'
        }}>
              As an admin, you manage your own membership directly instead of sending an approval request.
            </p>

            <div style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
              {teamSettings?.paymentMode ? <button type="button" className="btn btn--primary btn--sm" onClick={openRequestModal}>
                  Review My Membership Setup
                </button> : learnToRowConversionBlockedUntil ? <button type="button" className="btn btn--primary btn--sm" disabled>
                  Set Up My Membership
                </button> : <a href={`/join/${teamId}?manage=1`} className="btn btn--primary btn--sm">
                  Set Up My Membership
                </a>}

              {teamSettings?.paymentMode === 'subscription' ? <button type="button" className="btn btn--secondary btn--sm" onClick={handleOpenBillingPortal} disabled={portalLoading}>
                  {portalLoading ? 'Opening Billing...' : 'Manage Billing'}
                </button> : <a href="/dashboard" className="btn btn--secondary btn--sm">
                  Open Billing Dashboard
                </a>}
            </div>
          </> : <>
            <p className="text-muted" style={{
          marginBottom: '0.75rem'
        }}>
              Switch between individual and family, change who is covered, or change billing cadence here. Changes to
              billed or admin-managed tags still go to {adminLabel} for approval.
            </p>

            <div style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap'
        }}>
              <button type="button" className="btn btn--primary btn--sm" onClick={openRequestModal} disabled={requestSubmitting || Boolean(learnToRowConversionBlockedUntil)}>
                {activeRequest?.status === 'requested' ? 'Update Membership Request' : 'Change Membership'}
              </button>
            </div>
          </>)}

        <AdditionalAccounts teamId={teamId} revision={teamSettings?.additionalAccountsRevision} compact />

        {(canLeave || selfManaged) && <div style={{
        marginTop: '1rem',
        paddingTop: '0.9rem',
        borderTop: '1px solid #e5e7eb'
      }}>
            <p className="text-muted small" style={{
          marginBottom: '0.6rem'
        }}>
              If you wish to leave, please do so below.
            </p>
            <button type="button" className="btn btn--ghost btn--sm" onClick={handleLeaveClick} disabled={!selfManaged && typeof onLeave !== 'function'}>
              Leave Club
            </button>
          </div>}

        {requestStatus && <p className="text-muted" style={{
        marginTop: '0.75rem'
      }}>
            {requestStatus}
          </p>}
        {leaveStatus && <p className="text-muted" style={{
        marginTop: '0.75rem',
        marginBottom: 0
      }}>
            {leaveStatus}
          </p>}
        {requestError && <p className="modal-error" style={{
        marginTop: '0.75rem',
        marginBottom: 0
      }}>
            {requestError}
          </p>}
      </div>

      {requestModalOpen && <div className="modal-backdrop" onClick={() => !requestSubmitting && setRequestModalOpen(false)}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="membership-request-title" onClick={e => e.stopPropagation()} style={{
        maxWidth: '42rem'
      }}>
            <h2 id="membership-request-title" className="modal-title">
              {selfManaged ? 'Manage my membership' : 'Request membership changes'}
            </h2>
            {renewalSummary}

            {selfManaged && <p className="text-muted" style={{
          marginBottom: '1rem'
        }}>
                Existing memberships are changed here. Billing stays separate and only covers payment methods, invoices,
                and cancellations.
              </p>}

            {membershipMode === 'hybrid' && <fieldset className="form-group">
                <legend className="form-label">Payment route</legend>
                <p className="text-muted" style={{ marginTop: 0 }}>Route changes require administrator approval and take effect at the next renewal.</p>
                <label className="btn btn--secondary" style={{ justifyContent: 'flex-start', marginBottom: '0.5rem' }}>
                  <input type="radio" name="requestedCollectionRoute" checked={requestedCollectionRoute === 'stripe'} onChange={() => setRequestedCollectionRoute('stripe')} />
                  <span><strong>Online with Member Nest</strong> — Stripe payment and immediate confirmation</span>
                </label>
                <label className="btn btn--secondary" style={{ justifyContent: 'flex-start' }}>
                  <input type="radio" name="requestedCollectionRoute" checked={requestedCollectionRoute === 'offline'} onChange={() => setRequestedCollectionRoute('offline')} />
                  <span><strong>Offline through the club</strong> — pending administrator confirmation</span>
                </label>
              </fieldset>}

            <div className="form-group">
              <label className="form-label">Requested membership type</label>
              <div style={{
            display: 'grid',
            gap: '0.5rem'
          }}>
                <label className="btn btn--secondary" style={{
              justifyContent: 'flex-start'
            }}>
                  <input type="radio" name="requestedPlanType" checked={requestedPlanType !== 'family_tier'} onChange={() => setRequestedPlanType('individual')} />
                  <span>Individual Membership</span>
                </label>

                {familyTierOptions.map(tier => <label key={tier.id} className="btn btn--secondary" style={{
              justifyContent: 'flex-start'
            }}>
                    <input type="radio" name="requestedPlanType" checked={requestedPlanType === 'family_tier' && (requestedPlanTierId || selectedFamilyTier?.id) === tier.id} onChange={() => {
                setRequestedPlanType('family_tier');
                setRequestedPlanTierId(tier.id);
                if (!requestedFamilyId && familyGroups[0]?.familyId) {
                  setRequestedFamilyId(familyGroups[0].familyId);
                }
              }} />
                    <span>{tier.label} up to {tier.maxParents} parent(s), {tier.maxChildren} child(ren)</span>
                  </label>)}
              </div>
            </div>

            {requestedPlanType === 'individual' && (pricingTagGroups.length > 0 || lockedLearnToRowPricingTag) && <div className="form-group">
                <span className="form-label">Pricing-affecting tags</span>
                <p className="text-muted" style={{
            marginTop: '0.25rem',
            marginBottom: '0.75rem'
          }}>
                  {lockedLearnToRowPricingTag ? 'This Learn to Row conversion is fixed to the course membership option below.' : 'Only tags that change membership pricing are shown here.'}
                </p>
                {lockedLearnToRowPricingTag ? <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '0.75rem',
              background: '#f8fafc'
            }}>
                    <strong>{lockedLearnToRowPricingTag}</strong>
                  </div> : pricingTagGroups.map(group => <fieldset key={group.category} className="tag-category" style={{
            marginBottom: '0.75rem'
          }}>
                    <legend>
                      {group.category}
                      {!group.multiAssignable ? ' (choose one)' : ''}
                      {requiredPricingTagGroups.some(requiredGroup => requiredGroup.category === group.category) ? ' (required)' : ''}
                    </legend>
                    <div className="tag-options" style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}>
                      {group.tags.map(tag => {
                const selected = requestedPricingTagsByCategory[group.category]?.includes(tag.name);
                return <label key={`${group.category}:${tag.name}`} className={`tag-pill ${selected ? 'selected' : ''}`}>
                            <input type="checkbox" checked={Boolean(selected)} onChange={() => toggleRequestedPricingTag(group.category, tag.name, group.multiAssignable)} />
                            {tag.name}
                            {!tag.selfAssignable ? ' (requires approval)' : ''}
                          </label>;
              })}
                    </div>
                  </fieldset>)}
              </div>}

            {requestedPlanType === 'family_tier' && <div className="form-group">
                <span className="form-label">Family membership details</span>
                {familySelectionLoading ? <p className="text-muted">Loading family options…</p> : familyGroups.length === 0 ? <>
                    <p className="text-muted" style={{
              marginBottom: '0.5rem'
            }}>
                      No parent-managed family was found on your profile. Create or update a family first, then request
                      a family membership here.
                    </p>
                    <div>
                      <a href="/profile?tab=family" className="btn btn--secondary btn--sm">
                        Edit Family In Profile
                      </a>
                    </div>
                  </> : <>
                    {familyGroups.length > 1 && <label className="form-group">
                        <span className="form-label">Choose family</span>
                        <select className="form-input" value={requestedFamilyId || activeFamilyGroup?.familyId || ''} onChange={e => setRequestedFamilyId(e.target.value)}>
                          {familyGroups.map(group => <option key={group.familyId} value={group.familyId}>
                              {group.familyName}
                            </option>)}
                        </select>
                      </label>}

                    {selectedFamilyTier && <p className="text-muted" style={{
              marginBottom: '0.75rem'
            }}>
                        You are always included as parent 1. Add extra parents and children you want covered.
                      </p>}

                    {parentOptions.length > 1 && <div style={{
              marginBottom: '0.75rem'
            }}>
                        <strong style={{
                display: 'block',
                marginBottom: '0.35rem'
              }}>
                          Extra parents
                        </strong>
                        <div style={{
                display: 'grid',
                gap: '0.35rem'
              }}>
                          {parentOptions.filter(member => member.uid !== user?.uid).map(member => {
                  const checked = selectedTargetSet.has(member.uid);
                  const maxExtraParents = Math.max(0, Number(selectedFamilyTier?.maxParents || 1) - 1);
                  const disabled = !checked && selectedParentCount >= maxExtraParents;
                  return <label key={member.uid} style={{
                    display: 'flex',
                    gap: '0.45rem',
                    alignItems: 'center'
                  }}>
                                  <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleRequestedTarget(member.uid)} />
                                  <span>{member.name}</span>
                                </label>;
                })}
                        </div>
                      </div>}

                    {childOptions.length > 0 && <div style={{
              marginBottom: '0.75rem'
            }}>
                        <strong style={{
                display: 'block',
                marginBottom: '0.35rem'
              }}>
                          Children
                        </strong>
                        <div style={{
                display: 'grid',
                gap: '0.35rem'
              }}>
                          {childOptions.map(member => {
                  const checked = selectedTargetSet.has(member.uid);
                  const maxChildren = Math.max(0, Number(selectedFamilyTier?.maxChildren || 0));
                  const disabled = !checked && selectedChildCount >= maxChildren;
                  return <label key={member.uid} style={{
                    display: 'flex',
                    gap: '0.45rem',
                    alignItems: 'center'
                  }}>
                                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleRequestedTarget(member.uid)} />
                                <span>{member.name}</span>
                              </label>;
                })}
                        </div>
                      </div>}
                  </>}
              </div>}

            {cadenceOptions.length > 1 && <div className="form-group">
                <span className="form-label">Billing frequency</span>
                <div style={{
            display: 'grid',
            gap: '0.5rem'
          }}>
                  {cadenceOptions.map(option => <label key={option.key} className="btn btn--secondary" style={{
              justifyContent: 'flex-start'
            }}>
                      <input type="radio" name="selectedCadence" checked={selectedCadence?.key === option.key} onChange={() => setSelectedCadenceKey(option.key)} />
                      <span>
                        {formatCadenceOptionLabel(option.label, option.discountPct)}
                      </span>
                    </label>)}
                </div>
              </div>}

            <label className="form-group">
              <span className="form-label">Discount code (optional)</span>
              <input className="form-input" value={membershipDiscountCode} onChange={e => setMembershipDiscountCode(e.target.value.toUpperCase())} placeholder="Example: L2R50" />
            </label>

            {!selfManaged && <label className="form-group">
                <span className="form-label">Anything admins should know? (optional)</span>
                <textarea className="form-textarea" rows={4} value={requestNote} onChange={e => setRequestNote(e.target.value)} placeholder="Example: Please switch this from next month." />
              </label>}

            <div style={{
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '0.75rem',
          background: '#f8fafc'
        }}>
              <strong>{previewLoading ? 'Checking change…' : getPreviewOutcomeLabel(previewResult) || 'Change preview'}</strong>
              <p style={{
            margin: '0.5rem 0 0'
          }}>
                {previewLoading ? 'Working out whether this applies immediately, needs payment, or should wait until renewal.' : previewSummary}
              </p>
              {isInitialLearnToRowSetup && <p className="text-muted small" style={{
            margin: '0.5rem 0 0'
          }}>
                  Choose below whether you want to set this up as a recurring subscription or pay manually.
                </p>}
              {learnToRowConversionSummary ? <p className="text-muted small" style={{
            margin: '0.5rem 0 0'
          }}>
                  {learnToRowConversionSummary}
                </p> : null}
              {membershipPricingSummary ? <p className="text-muted small" style={{
            margin: '0.5rem 0 0'
          }}>
                  {membershipPricingSummary}
                </p> : null}
            </div>

            {requestError && <p className="modal-error">{requestError}</p>}

            <div className="form-actions">
              <button type="button" className="button button-secondary" onClick={() => setRequestModalOpen(false)} disabled={requestSubmitting}>
                Cancel
              </button>
              {isInitialLearnToRowSetup && <button type="button" className="button button-secondary" onClick={handleStartRecurringMembership} disabled={requestSubmitting || previewLoading}>
                  {requestSubmitting ? 'Saving...' : 'Set Up Recurring Subscription'}
                </button>}
              {teamSettings?.paymentMode === 'subscription' && <button type="button" className="button button-secondary" onClick={handleOpenBillingPortal} disabled={requestSubmitting || portalLoading}>
                  {portalLoading ? 'Opening Billing...' : 'Manage Billing'}
                </button>}
              <button type="button" className="button button-primary" onClick={handleSubmitMembershipChange} disabled={requestSubmitting || previewLoading}>
                {requestSubmitting ? 'Saving...' : submitButtonLabel}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}
ManageMembershipCard.propTypes = {
  teamId: PropTypes.string.isRequired,
  teamName: PropTypes.string.isRequired,
  memberName: PropTypes.string,
  adminIds: PropTypes.arrayOf(PropTypes.string),
  adminNames: PropTypes.arrayOf(PropTypes.string),
  allTags: PropTypes.array,
  memberTags: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  selfManaged: PropTypes.bool,
  reconciliationEnabled: PropTypes.bool,
  membershipMode: PropTypes.oneOf(['hosted_stripe', 'hybrid', 'club_gated', 'club_pay_later']),
  autoOpen: PropTypes.bool,
  onAutoOpenHandled: PropTypes.func,
  canLeave: PropTypes.bool,
  onLeave: PropTypes.func
};
